import { assetUrl } from "@/i18n/assets";
import { Link } from "@/i18n/Link";
import { ArrowRight, Award, MapPin, Users, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageLayout } from "@/components/site/PageLayout";
import { Seo, breadcrumbJsonLd, organizationJsonLd } from "@/components/site/Seo";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { VideoLightbox } from "@/components/site/VideoLightbox";
import { useCopy } from "@/i18n/LocaleContext";

const COPY = {
  en: {
    seoTitle: "About Our Trailer Axle Shop in Elkhart, IN | Axle Inc.",
    seoDesc: "Locally owned trailer axle and parts shop on Co Rd 9 in Elkhart, IN. 60+ years of combined experience and the area's only authorized Dexter Group distributor.",
    navHome: "Home",
    navAbout: "About",
    heroHeading: "A trailer-parts shop run by people who actually fix trailers.",
    heroSub: "For more than sixty years combined, the team behind Axle Inc. has been Elkhart County's trusted source for trailer axles, suspension, brakes, and the kind of straight answers that get a customer back on the road the same day.",
    videoTitle: "Quality trailer axle service at Axle Inc.",
    videoCaption: "Watch: Inside the Axle Inc. shop",
    builtHeading: "Built in Elkhart, for Elkhart.",
    builtBody: "Elkhart County is the recreational-vehicle and trailer capital of the country. Living and working here, you understand trailers in a way you can't get anywhere else. Axle Inc. was founded to be the local shop that consumers, fleets, dealers, and manufacturers could call when they needed an axle, a hub, a brake, or an answer - fast.",
    dexterHeading: "Authorized Dexter Group Distributor",
    dexterBody: "Axle Inc. is the area's exclusive distributor for Dexter Group, the leading North American manufacturer of trailer axles, brakes, and suspension. That partnership means our customers get genuine OEM components, current Dexter rebates, and engineering support straight from the source - paired with the local relationship and same-day service of an Elkhart shop.",
    whatWeDoHeading: "What we do, every day",
    whatWeDoList: [
      "Spec, source, and stock trailer axles from 1,000 to 10,000 lb capacity.",
      "Repair bearings, brakes, and spindles, and replace worn or damaged axles in our service bay.",
      "Supply parts to local dealers, manufacturers, and fleets - by the each or by the pallet.",
      "Help weekend boat-trailer owners walk in, get the right hub kit, and walk out."
    ],
    familyHeading: "Family feel. Industrial-grade work.",
    familyBody: "We answer the phone. We remember your name. We tell you when a part can wait and when it can't. That is the whole pitch. Don't take our word for it -",
    reviewsLink: "read our customer reviews",
    stats: [
      { stat: "60+", label: "Years combined experience" },
      { stat: "1", label: "Exclusive Dexter distributor for Elkhart area" },
      { stat: "1000s", label: "Customers served, big and small" },
      { stat: "Elkhart", label: "Indiana's trailer capital, our home" }
    ],
    ctaHeading: "Ready to talk parts?",
    ctaBody: "Call the shop, drop a message, or schedule service online. We'll be straight with you.",
    btnContact: "Contact us",
    btnProducts: "Browse products"
  },
  es: {
    seoTitle: "Sobre nuestra tienda de ejes para remolque en Elkhart, IN | Axle Inc.",
    seoDesc: "Tienda de ejes y piezas para remolque de propiedad local en Co Rd 9 en Elkhart, IN. Más de 60 años de experiencia combinada y el único distribuidor autorizado de Dexter Group en el área.",
    navHome: "Inicio",
    navAbout: "Sobre nosotros",
    heroHeading: "Una tienda de piezas para remolque dirigida por personas que realmente reparan remolques.",
    heroSub: "Durante más de sesenta años combinados, el equipo detrás de Axle Inc. ha sido la fuente de confianza del condado de Elkhart para ejes de remolque, suspensión, frenos y el tipo de respuestas directas que hacen que un cliente vuelva a la carretera el mismo día.",
    videoTitle: "Servicio de calidad para ejes de remolque en Axle Inc.",
    videoCaption: "Ver: Dentro del taller de Axle Inc.",
    builtHeading: "Construido en Elkhart, para Elkhart.",
    builtBody: "El condado de Elkhart es la capital nacional de vehículos recreativos y remolques. Viviendo y trabajando aquí, usted entiende los remolques de una manera que no puede obtener en ningún otro lugar. Axle Inc. fue fundada para ser la tienda local a la que consumidores, flotas, concesionarios y fabricantes pudieran llamar cuando necesitaran un eje, una maza, frenos o una respuesta, rápidamente.",
    dexterHeading: "Distribuidor autorizado de Dexter Group",
    dexterBody: "Axle Inc. es el distribuidor exclusivo en el área de Dexter Group, el principal fabricante norteamericano de ejes de remolque, frenos y suspensión. Esa asociación significa que nuestros clientes obtienen componentes OEM originales, reembolsos actuales de Dexter y soporte de ingeniería directamente de la fuente, junto con la relación local y el servicio en el mismo día de una tienda de Elkhart.",
    whatWeDoHeading: "Lo que hacemos, todos los días",
    whatWeDoList: [
      "Especificar, buscar y almacenar ejes de remolque con capacidad de 1,000 a 10,000 lb.",
      "Reparar rodamientos, frenos y ejes (spindles), y reemplazar ejes desgastados o dañados en nuestra bahía de servicio.",
      "Suministrar piezas a concesionarios locales, fabricantes y flotas, por unidad o por palé.",
      "Ayudar a los propietarios de remolques de botes de fin de semana a entrar, obtener el kit de maza correcto y salir.",
    ],
    familyHeading: "Ambiente familiar. Trabajo de grado industrial.",
    familyBody: "Contestamos el teléfono. Recordamos su nombre. Le decimos cuándo una pieza puede esperar y cuándo no. Ese es todo nuestro argumento. No tome solo nuestra palabra -",
    reviewsLink: "lea nuestras reseñas de clientes",
    stats: [
      { stat: "60+", label: "Años de experiencia combinada" },
      { stat: "1", label: "Distribuidor exclusivo de Dexter para el área de Elkhart" },
      { stat: "1000s", label: "Clientes atendidos, grandes y pequeños" },
      { stat: "Elkhart", label: "La capital de remolques de Indiana, nuestro hogar" }
    ],
    ctaHeading: "¿Listo para hablar de piezas?",
    ctaBody: "Llame a la tienda, envíe un mensaje o programe el servicio en línea. Seremos directos con usted.",
    btnContact: "Contáctenos",
    btnProducts: "Ver productos"
  }
};

export default function About() {
  const t = useCopy(COPY);
  return (
    <PageLayout>
      <Seo
        title={t.seoTitle}
        description={t.seoDesc}
        path="/about/"
        jsonLd={[organizationJsonLd(), breadcrumbJsonLd([
          { name: t.navHome, path: "/" },
          { name: t.navAbout, path: "/about/" },
        ])]}
      />
      <section className="bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-4 py-14 md:py-20">
          <div className="mb-6 [&_a]:text-secondary-foreground/70 [&_a:hover]:text-white [&_span]:text-white">
            <Breadcrumbs trail={[{ name: t.navHome, path: "/" }, { name: t.navAbout, path: "/about/" }]} />
          </div>
          <p className="text-xs md:text-sm uppercase tracking-[0.22em] text-white font-semibold mb-4">{t.navAbout}</p>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight max-w-3xl">
            {t.heroHeading}
          </h1>
          <p className="mt-5 text-lg text-secondary-foreground/80 max-w-2xl leading-relaxed">
            {t.heroSub}
          </p>
          <div className="mt-10 max-w-3xl">
            <VideoLightbox
              src={assetUrl("/media/quality-trailer-axle-service.webm")}
              poster={assetUrl("/media/quality-trailer-axle-service-poster.jpg")}
              title={t.videoTitle}
              caption={t.videoCaption}
            />
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 md:py-20 max-w-4xl">
        <div className="prose prose-neutral max-w-none">
          <h2>{t.builtHeading}</h2>
          <p>
            {t.builtBody}
          </p>
          <h2>{t.dexterHeading}</h2>
          <p>
            {t.dexterBody}
          </p>
          <h2>{t.whatWeDoHeading}</h2>
          <ul>
            {t.whatWeDoList.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
          <h2>{t.familyHeading}</h2>
          <p>
            {t.familyBody}{" "}
            <Link to="/reviews/">{t.reviewsLink}</Link>.
          </p>
        </div>
      </section>

      <section className="bg-muted/40 border-y border-border">
        <div className="container mx-auto px-4 py-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { icon: Wrench, ...t.stats[0] },
            { icon: Award, ...t.stats[1] },
            { icon: Users, ...t.stats[2] },
            { icon: MapPin, ...t.stats[3] },
          ].map(({ icon: Icon, stat, label }) => (
            <div key={label} className="flex flex-col items-start gap-2">
              <Icon className="h-6 w-6 text-primary" />
              <div className="text-3xl font-bold">{stat}</div>
              <p className="text-sm text-muted-foreground leading-relaxed">{label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3">{t.ctaHeading}</h2>
        <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
          {t.ctaBody}
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="/contact/">{t.btnContact} <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/products/">{t.btnProducts}</Link>
          </Button>
        </div>
      </section>
    </PageLayout>
  );
}
